import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { getTelemetry, sendCommand as apiSendCommand } from "../lib/api";
import { DEFAULT_SETTINGS } from "../lib/constants";
import { toast } from "sonner";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const loadLS = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export function AppProvider({ children }) {
  const [theme, setTheme] = useState(() => loadLS("cem_theme", "dark"));
  const [telemetry, setTelemetry] = useState(null);
  const [mqttConnected, setMqttConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [settings, setSettings] = useState(() => ({ ...DEFAULT_SETTINGS, ...loadLS("cem_settings", {}) }));
  const [snapshots, setSnapshots] = useState(() => loadLS("cem_gallery", []));
  const failRef = useRef(0);

  // theme application
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    localStorage.setItem("cem_theme", JSON.stringify(theme));
  }, [theme]);

  useEffect(() => { localStorage.setItem("cem_settings", JSON.stringify(settings)); }, [settings]);
  useEffect(() => { localStorage.setItem("cem_gallery", JSON.stringify(snapshots)); }, [snapshots]);

  // telemetry polling
  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const data = await getTelemetry();
        if (!active) return;
        setTelemetry(data);
        setMqttConnected(true);
        setLastUpdate(new Date());
        failRef.current = 0;
      } catch {
        failRef.current += 1;
        if (failRef.current > 2 && active) setMqttConnected(false);
      }
    };
    poll();
    const id = setInterval(poll, 2000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const toggleTheme = useCallback(() => setTheme((t) => (t === "dark" ? "light" : "dark")), []);

  const updateSettings = useCallback((patch) => setSettings((s) => ({ ...s, ...patch })), []);

  const resetSettings = useCallback(() => {
    setSettings({ ...DEFAULT_SETTINGS });
    toast.success("Configuration reset to defaults");
  }, []);

  const sendCommand = useCallback(async (device, action, label) => {
    try {
      await apiSendCommand(device, action);
      toast.success(`Sent → ${label || `${device}: ${action}`}`, { description: "Published to MQTT topic" });
    } catch {
      toast.error("Command failed — MQTT offline");
    }
  }, []);

  const addSnapshot = useCallback((dataUrl) => {
    const item = { id: Date.now().toString(), url: dataUrl, ts: new Date().toISOString() };
    setSnapshots((s) => [item, ...s]);
    toast.success("Snapshot saved to gallery");
  }, []);

  const deleteSnapshot = useCallback((id) => setSnapshots((s) => s.filter((x) => x.id !== id)), []);
  const clearSnapshots = useCallback(() => setSnapshots([]), []);

  const value = {
    theme, toggleTheme,
    telemetry, mqttConnected, lastUpdate,
    settings, updateSettings, resetSettings,
    sendCommand,
    snapshots, addSnapshot, deleteSnapshot, clearSnapshots,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
